/**
 * Partner-Benefits-Checkliste / "Was du wirklich bekommst".
 *
 * Premium Händler-Pitch-Szene mit grünen Häkchen-Karten und Stat-Highlights.
 * Designt als Antwort auf "Pain"-Beats davor — diese Szene zeigt klipp und
 * klar, was Anbieter & Händler bei Allyver bekommen (Modelle, Kosten, Speed,
 * Freiheit). Inhalte folgen direkt der Partner-Seite:
 *   https://allyver.com/become-partner
 *
 * Item-Format (alles via `inputs.items: string[]`):
 *   "Headline"                                  → Check-Karte mit nur Headline
 *   "Headline | Beschreibung"                   → Check-Karte mit Headline + Sub
 *   "0 € | Startkosten | stat"                  → Stat-Highlight-Karte (große Zahl)
 *   "12 € | Flatrate · 0% Provision | stat"     → Stat-Highlight-Karte
 *   "Label | block"                             → Akzent-Block (Backward-Compat)
 *   "Label | media"                             → Bild-Tile (Backward-Compat)
 *   "Label | text"                              → Check-Karte (Backward-Compat)
 *
 * Standard-Look: 1 Spalte in 9:16, 2 Spalten in 16:9. Stat-Karten spannen
 * 2 Spalten in 16:9, in 9:16 stehen sie eh über die volle Breite. Karten
 * sind dunkle Glas-Pills mit subtilem Rahmen, der Check-Badge ist ein
 * leuchtend grünes (#22c55e) Kreis-Icon mit weißem Häkchen.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
  pickMediaSrc,
} from "../../reel/sceneShared.js";
import { splitItem } from "./_mockHelpers.js";

// Universal-Erfolgs-Grün für die Häkchen. Bewusst NICHT brand-primary,
// damit das "✓ = bekommst du sicher"-Signal sofort lesbar ist, auch wenn
// das Reel-Akzent türkis/lila/pink ist.
const CHECK_GREEN = "#22c55e";
const CHECK_GREEN_SOFT = "#16a34a";

type TileKind = "check" | "stat" | "block" | "media";

interface ParsedTile {
  kind: TileKind;
  title: string;
  sub: string;
}

function parseTile(raw: string): ParsedTile {
  const parts = splitItem(raw);
  const a = parts[0] ?? "";
  const b = parts[1] ?? "";
  const c = parts[2] ?? "";

  // Drei Teile: das letzte Token ist explizit der Kind-Hint (stat/check/block/media)
  if (c) {
    const k = c.toLowerCase();
    if (k === "stat") return { kind: "stat", title: a, sub: b };
    if (k === "block") return { kind: "block", title: a, sub: "" };
    if (k === "media") return { kind: "media", title: a, sub: "" };
    if (k === "check" || k === "text") return { kind: "check", title: a, sub: b };
    // unbekannter Kind-Hint → behandeln als check mit description = b
    return { kind: "check", title: a, sub: b };
  }

  // Zwei Teile: entweder "Label | <kind>" (Backward-Compat) oder "Headline | description"
  if (b) {
    const k = b.toLowerCase();
    if (k === "block") return { kind: "block", title: a, sub: "" };
    if (k === "media") return { kind: "media", title: a, sub: "" };
    if (k === "stat") return { kind: "stat", title: a, sub: "" };
    if (k === "text" || k === "check") return { kind: "check", title: a, sub: "" };
    // Sonst ist b die Beschreibung
    return { kind: "check", title: a, sub: b };
  }

  // Einteiler → einfache Check-Karte mit Headline
  return { kind: "check", title: a, sub: "" };
}

// Standard-Items, falls keine im JSON gesetzt sind — direkt von der
// Allyver-Partner-Seite gezogen.
const DEFAULT_PARTNER_BENEFITS: string[] = [
  "0 € | Startkosten · Keine Setup-Gebühr | stat",
  "0 % Provision möglich | Restaurants & Produkte – nur bei Erfolg",
  "12 € / Monat | Taxi · Hotel · Events · Inserate · Services (0% Provision)",
  "10 Min Setup | Online ohne Code, ohne Entwickler",
  "Monatlich kündbar | Keine Mindestlaufzeit, keine Knebelverträge",
  "Direkter Kundenkontakt | Deine Kunden, deine Marke – nicht unsere",
  "Made in Germany | DSGVO · deutscher Support · echte Menschen",
];

function checkBadgeSvg(size: number): string {
  // Kreis-Badge mit Häkchen, vollständig inline-SVG, damit es überall
  // skaliert und keine externen Assets braucht.
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" style="display:block;flex-shrink:0;filter:drop-shadow(0 0 ${Math.round(size * 0.3)}px ${withAlpha(CHECK_GREEN, 0.55)});">
    <defs>
      <linearGradient id="cg-grad-${size}" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="${CHECK_GREEN}"/>
        <stop offset="100%" stop-color="${CHECK_GREEN_SOFT}"/>
      </linearGradient>
    </defs>
    <circle cx="12" cy="12" r="11" fill="url(#cg-grad-${size})"/>
    <circle cx="12" cy="12" r="11" fill="none" stroke="rgba(255,255,255,0.35)" stroke-width="0.6"/>
    <path d="M7 12.6 L10.6 16 L17 9.4" stroke="#ffffff" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}

export const gridReveal: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const sub = inputs.subtext ?? "";
  const isVertical = ctx.format === "9:16";

  const rawItems = (inputs.items?.length ? inputs.items : DEFAULT_PARTNER_BENEFITS).slice(0, 8);
  const tiles = rawItems.map(parseTile);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);
  const xl = typeSize("xl", ctx.format);

  // Type-Skala für die Karten — dichter als die alten Tiles, aber immer
  // noch klar lesbar im Reel-Vorschau-Frame.
  const cardTitleSize = (isVertical ? md * 0.62 : md * 0.62);
  const cardSubSize = (isVertical ? sm * 0.78 : sm * 0.78);
  const statNumSize = (isVertical ? xl * 0.92 : xl * 0.85);
  const checkSize = isVertical ? 56 : 48;

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const tileIds = tiles.map((_, i) => `${clipPrefix}-tile-${i}`);
  const gridId = `${clipPrefix}-grid`;

  const t = makeTl(clipStartSec);
  // "allyver"-Backdrop für markenkonsistenten Look (Türkis-Glow oben links,
  // Lila unten rechts, Pink-Akzent unten Mitte, Punkt-Pattern + Vignette).
  // Sieht dadurch nicht "leer schwarz" aus und matched die anderen Szenen
  // im Reel (Logo, Hero, Idee usw.). Da der bottom-Glow lila ist und nicht
  // türkis-grün, reibt er sich auch nicht mehr mit den grünen Häkchen.
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  // Karten-Stil-Bausteine
  const cardBaseStyle = (extra: string): string =>
    `position:relative;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.06)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);border:1.5px solid ${withAlpha(themeColors.fg, 0.14)};border-radius:22px;box-shadow:0 18px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);${extra}`;

  let mediaCount = 0;
  const tileHtml = (tile: ParsedTile, i: number): string => {
    const id = tileIds[i]!;
    if (tile.kind === "stat") {
      // Stat-Karte: große Zahl/Label oben, kleine Beschreibung darunter.
      // Linksseitiger Akzent-Streifen in Akzentfarbe + Check-Badge oben rechts.
      return `<div id="${id}" style="${cardBaseStyle(`grid-column: span ${isVertical ? 1 : 2}; padding:${isVertical ? "22px 26px" : "20px 26px"};display:flex;align-items:center;gap:${isVertical ? 22 : 24}px;overflow:hidden;`)}">
        <div style="position:absolute;left:0;top:14%;bottom:14%;width:5px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.4)} 100%);border-radius:0 4px 4px 0;"></div>
        <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${statNumSize}px;line-height:0.95;letter-spacing:-3px;color:${themeColors.fg};text-shadow:0 0 40px ${withAlpha(accent, 0.35)};white-space:nowrap;">${escapeHtml(tile.title)}</div>
        <div style="flex:1;min-width:0;font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${cardSubSize}px;color:${withAlpha(themeColors.fg, 0.78)};line-height:1.25;letter-spacing:-0.2px;">${escapeHtml(tile.sub)}</div>
        <div style="margin-left:auto;">${checkBadgeSvg(Math.round(checkSize * 0.78))}</div>
      </div>`;
    }
    if (tile.kind === "block") {
      // Backward-Compat: Akzent-Block
      return `<div id="${id}" style="grid-column: span ${isVertical ? 1 : 1};background:linear-gradient(135deg, ${accent} 0%, ${withAlpha(accent, 0.5)} 100%);border-radius:22px;min-height:${isVertical ? 110 : 100}px;box-shadow:0 18px 40px ${withAlpha(accent, 0.4)};display:flex;align-items:center;justify-content:center;color:${themeColors.bg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${cardTitleSize}px;letter-spacing:-0.5px;text-align:center;padding:18px;">${escapeHtml(tile.title)}</div>`;
    }
    if (tile.kind === "media") {
      const src = pickMediaSrc(inputs.media ?? null, mediaCount);
      mediaCount += 1;
      const bg = src
        ? `background:url('${escapeHtml(src)}') center/cover no-repeat;`
        : `background:linear-gradient(135deg, ${withAlpha(accent, 0.4)} 0%, ${withAlpha(themeColors.fg, 0.05)} 100%);`;
      return `<div id="${id}" style="${bg}border-radius:22px;min-height:${isVertical ? 110 : 100}px;border:1.5px solid ${withAlpha(themeColors.fg, 0.12)};box-shadow:0 18px 40px rgba(0,0,0,0.3);"></div>`;
    }

    // Check-Karte (Standard): grünes Häkchen links, Headline + optionale Sub rechts.
    const subBlock = tile.sub
      ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.regular};font-size:${cardSubSize}px;color:${withAlpha(themeColors.fg, 0.7)};line-height:1.3;letter-spacing:-0.1px;">${escapeHtml(tile.sub)}</div>`
      : "";
    return `<div id="${id}" style="${cardBaseStyle(`padding:${isVertical ? "18px 22px" : "16px 22px"};display:flex;align-items:center;gap:${isVertical ? 18 : 18}px;`)}">
      ${checkBadgeSvg(checkSize)}
      <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:${isVertical ? 5 : 4}px;">
        <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${cardTitleSize}px;color:${themeColors.fg};line-height:1.15;letter-spacing:-0.5px;">${escapeHtml(tile.title)}</div>
        ${subBlock}
      </div>
    </div>`;
  };

  const tilesHtml = tiles.map(tileHtml).join("");

  // Grid-Layout: 1 Spalte in 9:16 (super-lesbar), 2 Spalten in 16:9.
  const cols = isVertical ? 1 : 2;
  const gridGap = isVertical ? 14 : 16;
  const gridMaxW = isVertical ? 880 : 1280;

  // Headline-Größe: leicht reduziert, damit darunter Platz für 6-8 Karten bleibt.
  const headSize = lg * (isVertical ? 0.70 : 0.78);
  const subSize2 = sm * 0.95;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 28 : 30}px;padding:${isVertical ? "120px 64px" : "72px 96px"};">
    <div style="display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 14 : 12}px;text-align:center;">
      ${sub ? `<div id="${subId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${subSize2}px;letter-spacing:6px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.7)};">${escapeHtml(sub)}</div>` : ""}
      ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.02;max-width:${isVertical ? 920 : 1100}px;text-wrap:balance;text-shadow:0 0 60px ${withAlpha(themeColors.fg, 0.25)};">${escapeHtml(headline)}</div>` : ""}
    </div>
    <div id="${gridId}" style="display:grid;grid-template-columns:repeat(${cols}, minmax(0, 1fr));gap:${gridGap}px;width:100%;max-width:${gridMaxW}px;">${tilesHtml}</div>
  </div>`;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 18 }) : "",
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 12 }) : "",
    ...tileIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 24, scale: 0.94 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.0));
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.12));
  // Saubere von-oben-nach-unten-Stagger statt random — wirkt dadurch
  // wie eine klare Checkliste, die abgehakt wird.
  tileIds.forEach((id, i) => {
    tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "raw:\"back.out(1.6)\"" }, 0.45 + i * 0.09));
  });
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
